/**
 * The order's shipping label, decided once for the whole order page: which
 * label is the order's current one, whether it can still be voided, and what
 * the order page's parcel cards and label windows show from it.
 *
 * metadata.shippingLabel is the label as the server last recorded it
 * (server/api-util/order-steps/steps/labels.js), with its status:
 *   label_created   bought, ready to use
 *   void_requested  the seller asked the carrier to void it; until the carrier
 *                   answers it is still the order's label (if the void is
 *                   refused it can still be used), shown as "Void requested"
 *   void_rejected   the carrier refused the void: the label stands
 *   label_canceled  voided: kept for the record, no longer the order's label
 */
import { RETURN_STATES, SHIPPED_TRANSITIONS } from './orderTransitions';

export const LABEL_STATUS = Object.freeze({
  CREATED: 'label_created',
  VOID_REQUESTED: 'void_requested',
  VOID_REJECTED: 'void_rejected',
  CANCELED: 'label_canceled',
});

// Carriers whose labels can be voided through EasyPost, with the days after
// purchase a label can still be voided: the server's CARRIER_VOID_WINDOWS
// (labels.js), kept equal by orderRules.parity.test.js.
export const CARRIER_VOID_WINDOWS = Object.freeze({ USPS: 30, UPS: 90 });

const DAY_MS = 24 * 60 * 60 * 1000;

const dataOf = transaction => transaction?.attributes?.protectedData || {};
const metaOf = transaction => transaction?.attributes?.metadata || {};

/**
 * The order's current shipping label, or null.
 *
 * @param {Object} transaction
 * @param {Object} [justBought] a label bought on this page that the order
 *   doesn't show yet: { transactionId, label } (state.shipping). Used only
 *   for the order it was bought for.
 */
export const currentLabelOf = (transaction, justBought = null) => {
  // Older orders kept the label in protectedData.
  const saved = metaOf(transaction).shippingLabel || dataOf(transaction).shippingLabel || null;
  const orderId = transaction?.id?.uuid;
  const fresh =
    justBought?.label && orderId && justBought.transactionId === orderId ? justBought.label : null;
  // The order's own record is the truth for the label it holds; a label it
  // doesn't hold yet was bought after the page read it.
  const label = fresh && (!saved || saved.labelId !== fresh.labelId) ? fresh : saved;
  return label && label.status !== LABEL_STATUS.CANCELED ? label : null;
};

/** The seller asked to void this label and the carrier hasn't answered. */
export const isVoidRequested = label => label?.status === LABEL_STATUS.VOID_REQUESTED;

/**
 * A label the seller can ship with: the current one, unless a void of it is
 * pending (a seller who asked to void it ships with their own tracking).
 */
export const labelToShipWith = (transaction, justBought = null) => {
  const label = currentLabelOf(transaction, justBought);
  return label && !isVoidRequested(label) ? label : null;
};

/**
 * Can the seller ask to void the order's label now? As the server's void step
 * decides (labels.js voidLabel): a label paid for on this order, not already
 * being voided, of a carrier that voids labels, within that carrier's window.
 */
export const canVoidLabel = (transaction, now = new Date()) => {
  const payment = dataOf(transaction).shippingLabelPayment;
  if (!payment?.labelId) return false;
  if (isVoidRequested(metaOf(transaction).shippingLabel)) return false;
  const windowDays = CARRIER_VOID_WINDOWS[String(payment.carrier || '').toUpperCase()];
  if (!windowDays) return false;
  const ageDays = (new Date(now).getTime() - new Date(payment.boughtAt).getTime()) / DAY_MS;
  return Number.isFinite(ageDays) && ageDays <= windowDays;
};

const trackingCodeOf = parcel => parcel?.trackingCode || parcel?.trackingNumber || null;

/**
 * What the order page's parcel cards and label windows show (OrderDetailsCards):
 *
 *   label           the order's current label (null without one)
 *   voidRequested   the label's void is pending: shown marked "Void requested"
 *   shipped         the parcel the order went out with: the label, or the
 *                   seller's own tracking (always, over a label being voided)
 *   showOutbound    the order's parcel card shows: it has a tracking number,
 *                   and the order has shipped or its label waits to go (not
 *                   for an order cancelled before it left)
 *   returnFirst     a return is under way, so its parcel comes first
 *   links           the delivery card's windows, in order: 'details' (the
 *                   buyer's delivery choice), 'label' (the seller's label),
 *                   'shipping' (how it travels), 'return' (the return)
 *
 * @param {Object} p
 * @param {Object} p.transaction
 * @param {Object} p.stateData the page's state data (processState, returnLabel, showReturnTracking)
 * @param {boolean} p.isProvider
 * @param {boolean} p.hasDeliveryInfo the order has a delivery choice to show
 * @param {Object} [p.justBought] see currentLabelOf
 */
export const orderParcels = ({
  transaction,
  stateData = {},
  isProvider,
  hasDeliveryInfo,
  justBought,
}) => {
  const label = currentLabelOf(transaction, justBought);
  const voidRequested = isVoidRequested(label);
  const manualTracking = metaOf(transaction).manualTracking || null;
  const shipped = voidRequested ? manualTracking || label : label || manualTracking;

  const hasShipped = (transaction?.attributes?.transitions || []).some(t =>
    SHIPPED_TRANSITIONS.includes(t.transition)
  );
  const awaitingShipment = stateData.processState === 'purchased';
  const showOutbound = !!trackingCodeOf(shipped) && (hasShipped || awaitingShipment);

  const returnLabel = stateData.returnLabel || null;
  const showReturnDetails =
    !!stateData.showReturnTracking &&
    !!returnLabel &&
    !!(
      returnLabel.trackingCode ||
      returnLabel.carrier ||
      returnLabel.returnType ||
      returnLabel.labelUrl
    );

  const links = [
    hasDeliveryInfo ? 'details' : null,
    isProvider && label ? 'label' : null,
    manualTracking || (!isProvider && label) ? 'shipping' : null,
    showReturnDetails ? 'return' : null,
  ].filter(Boolean);

  return {
    label,
    voidRequested,
    shipped,
    showOutbound,
    returnFirst: RETURN_STATES.includes(stateData.processState),
    links,
  };
};
